import {
  CheckCircleOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import { Button, Divider, Form, Input, Modal, Space, Spin } from 'antd';
import DearSmallIcon from 'assets/images/dear_small.png';
import ExcelSmallIcon from 'assets/images/excel_small.png';
import QuickbooksSmallIcon from 'assets/images/quickbooks_small.png';
import XeroSmallIcon from 'assets/images/xero_small.png';
import ZohoSmallIcon from 'assets/images/zoho_small.png';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { QuestionMarkTooltip } from 'components/Custom/QuestionMarkTooltip';
import { CardTitleLargeSpan, GraySpan } from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { getTransactionColumns } from 'components/Vat/Report/tables/transactions/components/TransactionTableColumns';
import { TransactionTableHeader } from 'components/Vat/Report/tables/transactions/components/TransactionTableHeader';
import { TransactionTableWrapper } from 'components/Vat/Report/tables/transactions/styles';
import dayjs from 'dayjs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import routes from 'routes';
import { DataLogStatus, FilterAccounTypes } from 'store/app/enums';
import {
  fetchReconciliationFiltersRequest,
  queryReconciliationRequest,
} from 'store/reconciliation/actions';
import {
  QueryFilters,
  ReconTotal,
  TransactionTypeFilter,
} from 'store/reconciliation/models';
import {
  getReconciliationAccounts,
  getReconciliationExceptionsSize,
  getReconciliationJournalsByType,
  getReconciliationMappingCodes,
  getReconciliationOthersSize,
  getReconciliationQueryLoading,
  getReconciliationReportId,
  getReconciliationTaxRates,
  getReconciliationTotalTaxExceptions,
  getReconciliationTotalTaxOthers,
  getReconciliationTotalTaxTransactions,
  getReconciliationTransactionsSize,
} from 'store/reconciliation/selectors';
import { VatReturnLog } from 'store/vat/models';
import { isOtherVatMappingLabel } from 'store/vat/models/vatReportData';
import styled from 'styled-components';
import { useNumberFormat } from 'utils/numberFormat';
import { PreferenceActions, PreferenceSections } from 'utils/preferences';
import { StatusGetter } from 'utils/status-getter';

const providerIcons: Record<string, string> = {
  xero: XeroSmallIcon,
  quickbooks: QuickbooksSmallIcon,
  zoho: ZohoSmallIcon,
  dear: DearSmallIcon,
  excel: ExcelSmallIcon,
};
const MAX_TAX_BALANCE_BF = '1000000000000000000000000000000000';

const ReconciliationLayout = styled.div`
  width: 100%;
  max-width: 1250px;
`;

/** Wrapper for PDF content; when class "generating-pdf" is added, buttons and Open/View are hidden in the capture */
const ReconciliationPdfContent = styled.div`
  &.generating-pdf button {
    display: none !important;
  }
  &.generating-pdf [data-pdf-hide] {
    display: none !important;
  }
`;

const MainSection = styled.div`
  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 32px;
  margin-bottom: 16px;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  background: var(--color-surface-raised, #ffffff);
  border: 1px solid #e3ebeb;
  border-radius: 8px;
  box-shadow: 0px 4px 10px 0px #0000000d;
  padding: 20px 24px;
  margin-bottom: 48px;
  margin-top: 30px;
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text-dark, #042a2a);
`;

const TopSectionStatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
`;

const StatBoxesContainer = styled.div`
  display: flex;
  flex: 1;
  gap: 130px;
  align-items: flex-start;
`;

const StatBox = styled.div<{ $haveBorder?: boolean }>`
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  ${({ $haveBorder }) =>
    $haveBorder
      ? `
    border-left: 1px solid var(--color-primary, #41aaaf);
    padding-left: 12px;
    margin-left: 0;
  `
      : ''}
`;

const StatValue = styled.span<{ $negative?: boolean; $green?: boolean }>`
  font-family: Inter;
  font-size: 18px;
  font-weight: 400;
  color: ${({ $negative, $green }) =>
    $green ? 'var(--color-primary, #41aaaf)' : $negative ? '#d13131' : 'var(--color-text-dark, #042a2a)'};
`;

const ReconciledLabel = styled.span`
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-primary, #41aaaf);
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const WorkingsSection = styled.div`
  background: var(--color-surface-raised, #ffffff);
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 8px;
  overflow: hidden;
  padding-bottom: 24px;
`;

const ReconRow = styled.div<{
  $highlight?: 'light' | 'dark' | 'none';
  $indent?: boolean;
  $noBorder?: boolean;
  $taller?: boolean;
  $paddingBottom?: number;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ $taller }) => ($taller ? '20px 16px' : '12px 16px')};
  padding-bottom: ${({ $paddingBottom }) =>
    $paddingBottom != null ? `${$paddingBottom}px` : undefined};
  border-bottom: ${({ $noBorder }) =>
    $noBorder ? 'none' : '1px solid #e8f0f0'};
  font-size: 14px;
  background: ${({ $highlight }) =>
    $highlight === 'light'
      ? '#e8f5e9'
      : $highlight === 'dark'
      ? '#c8e6c9'
      : 'transparent'};

  &:last-child {
    border-bottom: none;
  }

  ${({ $indent }) => $indent && 'padding-left: 32px;'}
`;

const ReconLabel = styled.span<{ $bold?: boolean }>`
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
  font-family: Inter;
  color: var(--color-text-dark, #042a2a);
`;

const ReconValue = styled.span<{ $bold?: boolean; $red?: boolean }>`
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
  font-family: Inter;
  line-height: 1.2;
  white-space: nowrap;
  color: ${({ $red }) => ($red ? '#d13131' : 'var(--color-text-dark, #042a2a)')};
  text-align: right;
`;

const ReconSubTotal = styled.div<{
  $noBorder?: boolean;
  $paddingBottom?: number;
}>`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px ${({ $paddingBottom }) => $paddingBottom ?? 8}px 32px;
  border-bottom: ${({ $noBorder }) =>
    $noBorder ? 'none' : '1px solid #e8f0f0'};
  font-size: 14px;
  font-weight: 600;

  /* Single full-width line above both label and value. */
  &::before {
    content: '';
    position: absolute;
    left: 32px;
    right: 16px;
    top: 8px;
    border-top: 1px solid rgb(128, 128, 128);
    pointer-events: none;
  }
`;

const SubTotalValue = styled.span`
  display: block;
  text-align: right;
  min-width: 120px;
  padding-top: 4px;
`;

const SubTotalLabel = styled.span`
  display: block;
  padding-top: 4px;
  color: var(--color-text-dark, #042a2a);
`;

const SubTotalRowLabel = styled(ReconLabel)`
  display: block;
  padding-top: 4px;
`;

const TotalUnsubmittedRow = styled(ReconRow)`
  position: relative;

  /* ReconRow (default) uses padding: 12px 16px; align border to current label/value padding-top. */
  &::before {
    content: '';
    position: absolute;
    left: 16px;
    right: 16px;
    top: 12px;
    border-top: 1px solid rgb(128, 128, 128);
    pointer-events: none;
  }
`;

/** Same top rule as SubTotalValue, sized to match Total Unsubmitted Transactions row */
const TotalUnsubmittedValue = styled(SubTotalValue)`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-dark, #042a2a);
`;

const SectionHeader = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-dark, #042a2a);
  padding: 12px 16px;
`;

const SectionHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-dark, #042a2a);
  padding: 12px 16px;
`;

const WorkingsTitle = styled.div`
  font-size: 18px;
  font-family: Inter;
  font-weight: 400;
  color: var(--color-text-dark, #042a2a);
  padding: 20px 16px;
  border-bottom: 1px solid #e8f0f0;
`;

const OverpaidUnderpaidRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Match horizontal gutters used by subtotal/total rows so the value aligns. */
  padding: 20px 16px 20px 32px;
  font-size: 18px;
  background: #e5ebe5;
`;

const Spacer = styled.div<{ $height?: number }>`
  height: ${({ $height }) => $height ?? 24}px;
`;

const BalanceBfInputWrapper = styled.span<{ $viewReadOnly?: boolean }>`
  .ant-input-number {
    background-color: var(--color-surface, #f3f7f8);
  }
  .ant-input-number-input {
    text-align: right;
    background-color: var(--color-surface, #f3f7f8);
  }
  ${({ $viewReadOnly }) =>
    $viewReadOnly
      ? `
    .ant-input[disabled],
    .ant-input-disabled {
      color: var(--color-text-dark, #042a2a) !important;
      -webkit-text-fill-color: var(--color-text-dark, #042a2a);
      opacity: 1 !important;
      background-color: var(--color-surface, #f3f7f8) !important;
    }
    .ant-input-affix-wrapper-disabled .ant-input {
      color: var(--color-text-dark, #042a2a) !important;
      -webkit-text-fill-color: var(--color-text-dark, #042a2a);
      opacity: 1 !important;
    }
  `
      : ''}
`;

/** Apply button: always shows icon + "Apply", same colours as submissions filter save. */
const SaveButtonStyled = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 12px;
  background-color: var(--color-layout-bg, #e9f6f7);
  color: var(--color-text-dark, #042a2a);
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 50px;
  box-shadow: none;
  &:hover {
    background-color: var(--color-primary, #41aaaf) !important;
    color: #fff !important;
    border-color: var(--color-primary, #41aaaf) !important;
  }
`;

/** Disabled save button: same look, no hover. */
const DisabledSaveButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 26px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: var(--color-layout-bg, #e9f6f7);
  color: var(--color-text-dark, #042a2a);
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 50px;
  opacity: 0.5;
  cursor: default;
`;

export type ReconciliationTransaction = { label: string; value: number };

const VIEW_DUMMY_OPEN_SUBMISSION = {
  uuid: '00000000-0000-4000-8000-000000000001',
  status: DataLogStatus.AWAITING_CLIENT_APPROVAL,
  mappingId: '__recon_view_dummy__',
  fromReportingDate: '2024-10-01',
  toReportingDate: '2024-10-31',
  submissionDate: '2024-11-07',
  transactionsCount: 24,
  exceptionsCount: 2,
  totalTaxTransactions: 1250.5,
  totalTaxExceptions: -15.25,
} as unknown as VatReturnLog;

interface VatControlAccountReconciliationProps {
  organisation?: any;
  reconciliation?: Map<string, string>;
  a1Transactions?: ReconciliationTransaction[];
  asAtDate?: string;
  currency?: string;
  mappingId?: string;
  selectedMaps?: {
    mappingId: string;
    returnTypeId: string;
    mappingName?: string;
    returnDueDate?: string | Date;
  }[];
  vatReturnLogs?: VatReturnLog[];
  onChangeDate?: () => void;
  /** Called when user clicks recalc icon; use to save balance b/f to audit / trigger recalc */
  onRecalculate?: (balanceBf: number) => void;
  onPublishReport?: () => void;
  /** Latest control-account totals for publish / save payload (includes {@link ReconTotal.savedStatus}). */
  onReconTotalSnapshot?: (reconTotal: ReconTotal) => void;
  /** Called when user clicks Refresh Data */
  onRefreshData?: () => void;
  /** For loading/saving transaction table column preferences in the View modal */
  handlePreferences?: (
    section: import('utils/preferences').PreferenceSections,
    action: import('utils/preferences').PreferenceActions,
    preference: import('utils/preferences').Preference
  ) => any;
  /**
   * Saved-report view mode: use saved `dataLog` totals for headings/summary, but
   * query modal rows from the reconciliation query endpoint using the saved log id.
   */
  viewSavedDummyMode?: boolean;
  /**
   * When set (e.g. loaded saved report), overrides GenTax / provider / difference displays
   * from {@link ReconTotal} fields returned by the view API.
   */
  savedViewReconTotals?: Pick<
    ReconTotal,
    | 'gtBalance'
    | 'xeroBalance'
    | 'difference'
    | 'totalTaxBalance'
    | 'account'
    | 'openDataLogId'
    | 'openTransactionsCount'
    | 'openExceptionsCount'
    | 'totalTaxOpenTransactions'
    | 'totalTaxOpenExceptions'
    | 'savedStatus'
  >;
  /** From view `dataLog` — resolve return label like live mode (mapping + return types). */
  savedViewTaxReportTypeId?: string;
  savedViewMappingId?: string;
  /**
   * Saved-report view: unsubmitted totals/counts from the view API `dataLog`
   * ({@link VatReturnLog} counts + total tax fields).
   */
  savedViewDataLog?: VatReturnLog | null;
  /**
   * Log id sent to view-reconciliation API (table row). Strong fallback for VAT report URL when
   * `reconTotal.openDataLogId` / `dataLog.uuid` are missing.
   */
  savedViewFetchedLogId?: string | null;
  /**
   * Open-period from / to / due from `mappedReturns` (same row as mapping-id resolution).
   * Status for that block still comes from {@link savedViewReconTotals.savedStatus}.
   */
  savedViewOpenPeriodFromMappedReturns?: {
    fromReportingDate?: string | null;
    toReportingDate?: string | null;
    dueDate?: string | null;
  } | null;
}

function getReconValue(
  reconciliation: Map<string, string> | Record<string, string> | undefined,
  key: string
): string | undefined {
  if (!reconciliation) return undefined;
  const m = reconciliation as Map<string, string>;
  const r = reconciliation as Record<string, string>;
  return m.get?.(key) ?? r[key];
}

function firstNonEmptyString(
  ...parts: (string | null | undefined)[]
): string | undefined {
  for (const p of parts) {
    if (p == null) continue;
    const s = String(p).trim();
    if (s) return s;
  }
  return undefined;
}

function getReconCount(
  reconciliation: Map<string, string> | Record<string, string> | undefined,
  ...keys: string[]
): number | null {
  for (const key of keys) {
    const raw = getReconValue(reconciliation, key);
    if (raw != null && raw !== '') {
      const n = Number(String(raw).replace(/,/g, ''));
      if (!Number.isNaN(n) && n >= 0) return n;
    }
  }
  return null;
}

export const VatControlAccountReconciliation: React.FC<
  VatControlAccountReconciliationProps
> = ({
  organisation,
  reconciliation,
  a1Transactions,
  asAtDate,
  currency,
  mappingId,
  selectedMaps,
  vatReturnLogs,
  onChangeDate,
  onRecalculate,
  onReconTotalSnapshot,
  handlePreferences,
  viewSavedDummyMode = false,
  savedViewReconTotals,
  savedViewTaxReportTypeId,
  savedViewMappingId,
  savedViewDataLog,
  savedViewFetchedLogId,
  savedViewOpenPeriodFromMappedReturns,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const generatedTransactionsSizeRaw = useSelector(
    getReconciliationTransactionsSize
  );
  const generatedExceptionsSizeRaw = useSelector(
    getReconciliationExceptionsSize
  );
  const generatedOthersSizeRaw = useSelector(getReconciliationOthersSize);
  const reconciliationReportId = useSelector(getReconciliationReportId);
  const cachedTransactionJournals = useSelector(
    getReconciliationJournalsByType(TransactionTypeFilter.TRANSACTION)
  );
  const cachedExceptionJournals = useSelector(
    getReconciliationJournalsByType(TransactionTypeFilter.EXCEPTION)
  );
  const reconciliationQueryLoadingRaw = useSelector(
    getReconciliationQueryLoading
  );
  const reconciliationMappingCodesRaw = useSelector(
    getReconciliationMappingCodes
  );
  const reconciliationAccountsRaw = useSelector(getReconciliationAccounts);
  const reconciliationTaxRatesRaw = useSelector(getReconciliationTaxRates);
  const totalTaxTransactionsRaw = useSelector(
    getReconciliationTotalTaxTransactions
  );
  const totalTaxExceptionsRaw = useSelector(
    getReconciliationTotalTaxExceptions
  );
  const totalTaxOthersRaw = useSelector(getReconciliationTotalTaxOthers);

  const useSavedViewDataLog = Boolean(viewSavedDummyMode && savedViewDataLog);

  const generatedTransactionsSize = viewSavedDummyMode
    ? useSavedViewDataLog
      ? Number(savedViewDataLog!.transactionsCount ?? 0)
      : 0
    : generatedTransactionsSizeRaw;
  const generatedExceptionsSize = viewSavedDummyMode
    ? useSavedViewDataLog
      ? Number(savedViewDataLog!.exceptionsCount ?? 0)
      : 0
    : generatedExceptionsSizeRaw;
  const generatedOthersSize = viewSavedDummyMode
    ? useSavedViewDataLog
      ? Number(savedViewDataLog!.othersCount ?? 0)
      : 0
    : generatedOthersSizeRaw;
  const effectiveReportId = reconciliationReportId;

  const reconciliationQueryLoading = reconciliationQueryLoadingRaw;
  const totalTaxTransactions = viewSavedDummyMode
    ? useSavedViewDataLog
      ? savedViewDataLog!.totalTaxTransactions != null
        ? Number(savedViewDataLog!.totalTaxTransactions)
        : 0
      : 0
    : totalTaxTransactionsRaw;
  const totalTaxExceptions = viewSavedDummyMode
    ? useSavedViewDataLog
      ? savedViewDataLog!.totalTaxExceptions != null
        ? Number(savedViewDataLog!.totalTaxExceptions)
        : 0
      : 0
    : totalTaxExceptionsRaw;
  const totalTaxOthers = viewSavedDummyMode
    ? useSavedViewDataLog
      ? savedViewDataLog!.totalTaxOthers != null
        ? Number(savedViewDataLog!.totalTaxOthers)
        : 0
      : 0
    : totalTaxOthersRaw;

  const needsRemoteFilterData =
    (reconciliationMappingCodesRaw?.length || 0) === 0 ||
    (reconciliationAccountsRaw?.length || 0) === 0;

  const reconSource: Map<string, string> | Record<string, string> | undefined =
    reconciliation;

  const { formatNumber, formatWithParens, locale } = useNumberFormat();
  const [balanceBf, setBalanceBf] = React.useState<number>(0);
  const [balanceBfUsed, setBalanceBfUsed] = React.useState<number>(0);
  /** While non-null, input shows this string so user can type decimals (e.g. "1000." or "1000.75") without formatter stripping them. */
  const [balanceBfDisplay, setBalanceBfDisplay] = React.useState<string | null>(
    null
  );
  const [transactionsModalOpen, setTransactionsModalOpen] =
    React.useState(false);
  const [exportingPdf, setExportingPdf] = React.useState(false);

  const TRANSACTIONS_COLUMNS_CONFIG = 'transactions-columns-config';
  const defaultVisibleColumns = [
    'taxName',
    'journalCreatedDate',
    'journalDate',
    'sourceType',
    'accountCode',
    'accountType',
    'accountName',
    'contact',
    'invoiceNumber',
    'description',
    'reference',
    'currency',
  ];
  const [transactionsColumnConfig, setTransactionsColumnConfig] =
    React.useState<{
      visibleColumns: string[];
      lockedColumns: string[];
      columnOrder: string[] | undefined;
    }>({
      visibleColumns: defaultVisibleColumns,
      lockedColumns: [],
      columnOrder: undefined,
    });

  React.useEffect(() => {
    if (!transactionsModalOpen || !handlePreferences) return;
    const config = handlePreferences(
      PreferenceSections.TRANSACTIONS,
      PreferenceActions.GET,
      { name: TRANSACTIONS_COLUMNS_CONFIG }
    );
    if (
      config &&
      typeof config === 'object' &&
      Array.isArray(config.visibleColumns)
    ) {
      setTransactionsColumnConfig({
        visibleColumns: config.visibleColumns,
        lockedColumns: config.lockedColumns || [],
        columnOrder: config.columnOrder,
      });
    } else {
      setTransactionsColumnConfig({
        visibleColumns: defaultVisibleColumns,
        lockedColumns: [],
        columnOrder: undefined,
      });
    }
  }, [transactionsModalOpen, handlePreferences]);

  const handleTransactionsColumnsChange = React.useCallback(
    (config: {
      visibleColumns: string[];
      lockedColumns: string[];
      columnOrder: string[];
    }) => {
      setTransactionsColumnConfig(config);
      handlePreferences?.(
        PreferenceSections.TRANSACTIONS,
        PreferenceActions.SET,
        {
          name: TRANSACTIONS_COLUMNS_CONFIG,
          value: config,
        }
      );
    },
    [handlePreferences]
  );
  const [transactionsModalSection, setTransactionsModalSection] =
    React.useState<'not-submitted' | 'incomplete' | null>(null);
  const [transactionsModalViewMode, setTransactionsModalViewMode] =
    React.useState<'transaction' | 'exception' | 'other'>('transaction');
  const [transactionsModalPageSize, setTransactionsModalPageSize] =
    React.useState(20);
  const [transactionsModalPage, setTransactionsModalPage] = React.useState(1);
  const [transactionsModalFilters, setTransactionsModalFilters] =
    React.useState<QueryFilters | undefined>(undefined);
  const [transactionsModalScopeFilters, setTransactionsModalScopeFilters] =
    React.useState<Record<string, any>>({});
  const lastQueryKeyRef = React.useRef<string>('');
  const organisationTaxRates =
    organisation?.taxRatesUnique && organisation.taxRatesUnique.length > 0
      ? organisation.taxRatesUnique
      : organisation?.taxRates ?? [];
  const organisationTaxNameToIds = React.useMemo(() => {
    const map = new Map<string, string[]>();
    (organisationTaxRates || []).forEach((rate: any) => {
      const rawName = String(rate?.name ?? '').trim();
      const id = String(rate?.id ?? '').trim();
      if (!rawName || !id) return;
      const key = rawName.toLowerCase();
      const existing = map.get(key) || [];
      if (!existing.includes(id)) {
        existing.push(id);
        map.set(key, existing);
      }
    });
    return map;
  }, [organisationTaxRates]);
  const reconciliationCustomFilterOptions = React.useMemo<
    Record<string, { label: string; value: string | number }[]>
  >(() => {
    const accountCodeOptions =
      reconciliationAccountsRaw?.map((acc) => ({
        label: `${acc.code}${acc.name ? ` - ${acc.name}` : ''}`,
        value: acc.code,
      })) ?? [];
    const accountNameOptions =
      reconciliationAccountsRaw?.map((acc) => ({
        label: `${acc.name}${acc.code ? ` (${acc.code})` : ''}`,
        value: acc.name,
      })) ?? [];
    const accountTypeOptions = Array.from(
      new Set((reconciliationAccountsRaw || []).map((acc) => acc.type))
    ).map((type) => ({
      label: FilterAccounTypes[type] ?? type,
      value: type,
    }));

    const taxRateSource =
      organisationTaxRates && organisationTaxRates.length > 0
        ? organisationTaxRates
        : reconciliationTaxRatesRaw || [];
    return {
      selectedValue: (reconciliationMappingCodesRaw || []).map((code) => ({
        label: code,
        value: code,
      })),
      taxName: Array.from(
        new Set<string>(
          taxRateSource
            .map((rate) => String(rate?.name ?? '').trim())
            .filter((name): name is string => name.length > 0)
        )
      )
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({
          label: name,
          value: name,
        })),
      accountCode: accountCodeOptions,
      accountName: accountNameOptions,
      accountType: accountTypeOptions,
    };
  }, [
    organisationTaxRates,
    reconciliationAccountsRaw,
    reconciliationMappingCodesRaw,
    reconciliationTaxRatesRaw,
  ]);

  const entityKey = `${organisation?.connectionId ?? ''}-${mappingId ?? ''}`;
  React.useEffect(() => {
    if (viewSavedDummyMode) {
      const tb = savedViewReconTotals?.totalTaxBalance;
      const n = typeof tb === 'number' && Number.isFinite(tb) ? tb : 0;
      setBalanceBf(n);
      setBalanceBfUsed(n);
      setBalanceBfDisplay(null);
      setTransactionsModalOpen(false);
      setTransactionsModalSection(null);
      return;
    }
    const orgBalanceRaw = organisation?.organisationDetails?.totalTaxBalance;
    const orgBalance =
      typeof orgBalanceRaw === 'number'
        ? orgBalanceRaw
        : Number(String(orgBalanceRaw ?? '').replace(/,/g, ''));
    const initialBalance = Number.isFinite(orgBalance) ? orgBalance : 0;

    setBalanceBf(initialBalance);
    setBalanceBfUsed(initialBalance);
    setBalanceBfDisplay(null);
    setTransactionsModalOpen(false);
    setTransactionsModalSection(null);
  }, [
    viewSavedDummyMode,
    entityKey,
    organisation?.organisationDetails?.totalTaxBalance,
    savedViewReconTotals?.totalTaxBalance,
  ]);

  const formatBalanceBf = (n: number) =>
    Number.isNaN(n)
      ? ''
      : formatNumber(n, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  const balanceBfInputValue =
    balanceBfDisplay !== null ? balanceBfDisplay : formatBalanceBf(balanceBf);
  const isTaxBalanceTooLarge = React.useCallback(
    (rawValue: string): boolean => {
      const sanitized = String(rawValue ?? '')
        .replace(/,/g, '')
        .trim();
      if (
        !sanitized ||
        sanitized === '-' ||
        sanitized === '.' ||
        sanitized === '-.'
      )
        return false;
      if (!/^-?\d*\.?\d*$/.test(sanitized)) return false;
      if (sanitized.startsWith('-')) return false;

      const integerPartRaw = sanitized.split('.')[0] ?? '';
      const integerPart = integerPartRaw.replace(/^0+(?=\d)/, '') || '0';
      if (integerPart.length > MAX_TAX_BALANCE_BF.length) return true;
      if (integerPart.length < MAX_TAX_BALANCE_BF.length) return false;
      return integerPart > MAX_TAX_BALANCE_BF;
    },
    []
  );
  const isBalanceBfTooLarge = React.useMemo(
    () => isTaxBalanceTooLarge(balanceBfInputValue),
    [balanceBfInputValue, isTaxBalanceTooLarge]
  );
  const canApplyBalanceBf = balanceBf !== balanceBfUsed && !isBalanceBfTooLarge;
  const commitBalanceBfDisplay = React.useCallback(() => {
    if (balanceBfDisplay === null) return;
    const s = String(balanceBfDisplay).replace(/,/g, '');
    const n = parseFloat(s);
    setBalanceBf(Number.isNaN(n) ? 0 : n);
    setBalanceBfDisplay(null);
  }, [balanceBfDisplay]);

  const reconcileDate = React.useMemo(() => {
    if (!asAtDate) return null;
    const d = dayjs(asAtDate);
    return d.isValid() ? d : null;
  }, [asAtDate]);

  const returnName = React.useMemo(() => {
    if (viewSavedDummyMode) {
      const retTypes = organisation?.returnTypesEdit;
      if (savedViewMappingId && selectedMaps?.length && retTypes?.length) {
        const mapping = selectedMaps.find(
          (m) => m.mappingId === savedViewMappingId
        );
        if (mapping) {
          const rt = retTypes.find((r) => r.id === mapping.returnTypeId);
          const label = rt?.name ?? mapping.mappingName;
          if (label) return label;
        }
      }
      if (savedViewTaxReportTypeId && retTypes?.length) {
        const rt = retTypes.find((r) => r.id === savedViewTaxReportTypeId);
        if (rt?.name) return rt.name;
      }
      return '';
    }
    if (!mappingId || !selectedMaps?.length || !organisation?.returnTypesEdit)
      return '';
    const mapping = selectedMaps.find((m) => m.mappingId === mappingId);
    if (!mapping) return '';
    const rt = organisation.returnTypesEdit.find(
      (r: { id: string }) => r.id === mapping.returnTypeId
    );
    return rt?.name ?? mapping.mappingName ?? '';
  }, [
    viewSavedDummyMode,
    savedViewMappingId,
    savedViewTaxReportTypeId,
    mappingId,
    selectedMaps,
    organisation?.returnTypesEdit,
  ]);

  const isSubmittedOrCancelled = (status: string) => {
    const s = (status || '').trim();
    return (
      s === DataLogStatus.SUBMITTED ||
      s === DataLogStatus.CANCELLED ||
      s.toLowerCase() === 'submitted' ||
      s.toLowerCase() === 'cancelled'
    );
  };

  const lastSubmissionForMapping = React.useMemo(() => {
    if (viewSavedDummyMode) return null;
    if (!mappingId || !vatReturnLogs?.length) return null;
    const forMapping = vatReturnLogs
      .filter((log) => log.mappingId === mappingId)
      .sort((a, b) => {
        const da = a.generatedDate ? dayjs(a.generatedDate).valueOf() : 0;
        const db = b.generatedDate ? dayjs(b.generatedDate).valueOf() : 0;
        return db - da;
      });
    return forMapping.length > 0 ? forMapping[0] : null;
  }, [viewSavedDummyMode, mappingId, vatReturnLogs]);

  const openSubmission = React.useMemo(() => {
    if (viewSavedDummyMode) return VIEW_DUMMY_OPEN_SUBMISSION;
    const last = lastSubmissionForMapping;
    if (!last || isSubmittedOrCancelled(last.status)) return null;
    return last;
  }, [viewSavedDummyMode, lastSubmissionForMapping]);

  const dataLogId = viewSavedDummyMode
    ? firstNonEmptyString(
        savedViewReconTotals?.openDataLogId,
        savedViewFetchedLogId,
        savedViewDataLog?.uuid,
        (savedViewDataLog as { UUID?: string } | null | undefined)?.UUID
      )
    : openSubmission?.uuid;

  /** Token for `/vat/report/connection/...` — org connection, or fields on view `dataLog`. */
  const connectionIdForVatReport = firstNonEmptyString(
    organisation?.connectionId,
    viewSavedDummyMode ? savedViewDataLog?.applicationConnectionID : undefined,
    viewSavedDummyMode ? savedViewDataLog?.connectionId : undefined
  );

  /** Real mapping for VAT report deep link; live uses `mappingId` prop (`reconciliationRun`). Saved view: parent may pass log-row `mappingId` first, then `dataLog.mappingId`, then single selected map. */
  const mappingIdForVatReport = viewSavedDummyMode
    ? firstNonEmptyString(
        savedViewMappingId,
        savedViewDataLog?.mappingId,
        selectedMaps?.length === 1 ? selectedMaps[0].mappingId : undefined
      )
    : mappingId
    ? firstNonEmptyString(mappingId)
    : undefined;

  const vatReportOpenHref = React.useMemo(() => {
    if (!connectionIdForVatReport || !mappingIdForVatReport || !dataLogId)
      return null;
    const path = routes.vat.viewReportLink
      .replace(':connectionId', connectionIdForVatReport)
      .replace(':mappingId', mappingIdForVatReport)
      .replace(':dataLogId', dataLogId);
    return `${process.env.PUBLIC_URL || ''}${path}`;
  }, [connectionIdForVatReport, mappingIdForVatReport, dataLogId]);

  const hasOpenSubmission = viewSavedDummyMode
    ? true
    : Boolean(
        openSubmission &&
          dataLogId &&
          organisation?.connectionId &&
          mappingId &&
          !isSubmittedOrCancelled(openSubmission!.status)
      );

  /** Open-period header: saved view prefers store `mappedReturns` dates; status from `reconTotal.savedStatus`. */
  const openPeriodSummary = React.useMemo(() => {
    const currentMapping = selectedMaps?.find((m) => m.mappingId === mappingId);
    const fromStr = viewSavedDummyMode
      ? firstNonEmptyString(
          savedViewOpenPeriodFromMappedReturns?.fromReportingDate,
          savedViewDataLog?.fromReportingDate
        )
      : openSubmission?.fromReportingDate;
    const toStr = viewSavedDummyMode
      ? firstNonEmptyString(
          savedViewOpenPeriodFromMappedReturns?.toReportingDate,
          savedViewDataLog?.toReportingDate
        )
      : openSubmission?.toReportingDate;
    const rawDue = viewSavedDummyMode
      ? firstNonEmptyString(
          savedViewOpenPeriodFromMappedReturns?.dueDate,
          savedViewDataLog?.dueDate,
          savedViewDataLog?.submissionDate
        )
      : currentMapping?.returnDueDate ?? openSubmission?.submissionDate;
    const status = viewSavedDummyMode
      ? savedViewReconTotals?.savedStatus ?? null
      : openSubmission?.status;

    const from = fromStr ? dayjs(fromStr) : null;
    const to = toStr ? dayjs(toStr) : null;
    const periodStr =
      from?.isValid() && to?.isValid()
        ? `${from.format('DD MMM YYYY')} – ${to.format('DD MMM YYYY')}`
        : '-';
    const dueDate = rawDue ? dayjs(rawDue) : null;
    const dueStr = dueDate?.isValid()
      ? t('organisation.reconciliation-due-date', {
          date: dueDate.format('DD/MM/YYYY'),
        })
      : t('organisation.reconciliation-due-date-empty');

    return { periodStr, dueStr, status };
  }, [
    viewSavedDummyMode,
    savedViewOpenPeriodFromMappedReturns,
    savedViewDataLog,
    savedViewReconTotals,
    openSubmission,
    mappingId,
    selectedMaps,
    t,
  ]);

  // Unsubmitted totals should use the backend-provided reconciliation totals.
  // `a1Transactions` is optional and is still used elsewhere, but totals follow the Redux reconciliation state.
  const a1TotalTrans =
    totalTaxTransactions ??
    (a1Transactions?.length
      ? a1Transactions.reduce((s, t) => s + t.value, 0)
      : 0);
  const a1TotalExc = totalTaxExceptions ?? 0;
  const a1TotalOther = totalTaxOthers ?? 0;
  // Sub-totals must keep the sign so credits/deductions reduce the total.
  const a1TotalTransContribution = Number(a1TotalTrans ?? 0);
  const a1TotalExcContribution = Number(a1TotalExc ?? 0);
  const a1TotalOtherContribution = Number(a1TotalOther ?? 0);
  const a2TransactionCount =
    viewSavedDummyMode && savedViewReconTotals
      ? savedViewReconTotals.openTransactionsCount
      : viewSavedDummyMode
      ? 0
      : openSubmission?.transactionsCount;
  const a2ExceptionCount =
    viewSavedDummyMode && savedViewReconTotals
      ? savedViewReconTotals.openExceptionsCount
      : viewSavedDummyMode
      ? 0
      : openSubmission?.exceptionsCount;
  const a1TransactionCount = useSavedViewDataLog
    ? Number(savedViewDataLog!.transactionsCount ?? 0)
    : a1Transactions?.length ??
      getReconCount(
        reconSource as any,
        'a1TransactionCount',
        'notSubmittedTransactionCount'
      );
  const a1ExceptionCount = useSavedViewDataLog
    ? Number(savedViewDataLog!.exceptionsCount ?? 0)
    : getReconCount(
        reconSource as any,
        'a1ExceptionCount',
        'notSubmittedExceptionCount'
      );
  const pendingTransactionsCount =
    a1TransactionCount ?? generatedTransactionsSize;
  const pendingExceptionsCount = a1ExceptionCount ?? generatedExceptionsSize;
  const pendingOthersCount = generatedOthersSize ?? 0;

  const showA1TransactionsRow = (pendingTransactionsCount ?? 0) > 0;
  const showA1ExceptionsRow = (pendingExceptionsCount ?? 0) > 0;
  const showA1OthersRow = (pendingOthersCount ?? 0) > 0;
  const a1SubTotal =
    (showA1TransactionsRow ? a1TotalTransContribution : 0) +
    (showA1ExceptionsRow ? a1TotalExcContribution : 0) +
    (showA1OthersRow ? a1TotalOtherContribution : 0);
  const a2TotalTrans =
    viewSavedDummyMode && savedViewReconTotals
      ? savedViewReconTotals.totalTaxOpenTransactions
      : openSubmission?.totalTaxTransactions ?? 0;
  const a2TotalExc =
    viewSavedDummyMode && savedViewReconTotals
      ? savedViewReconTotals.totalTaxOpenExceptions
      : openSubmission?.totalTaxExceptions ?? 0;
  const showA2TransactionsRow = (a2TransactionCount ?? 0) > 0;
  const showA2ExceptionsRow = (a2ExceptionCount ?? 0) > 0;
  const a2SubTotal =
    (showA2TransactionsRow ? a2TotalTrans : 0) +
    (showA2ExceptionsRow ? a2TotalExc : 0);

  // In saved-view mode `hasOpenSubmission` is optimistic (dummy period),
  // but we still want to hide the entire open-period section when there
  // are no open transactions or exceptions.
  const showOpenPeriodSection = hasOpenSubmission
    ? showA2TransactionsRow || showA2ExceptionsRow
    : false;

  const totalUnsubmitted = a1SubTotal + a2SubTotal;
  const totalA = balanceBfUsed + totalUnsubmitted;

  const salesTaxRaw = getReconValue(reconSource as any, 'salesTax');
  const balanceB = React.useMemo(() => {
    if (salesTaxRaw != null && salesTaxRaw !== '') {
      const n = Number(String(salesTaxRaw).replace(/,/g, ''));
      if (!Number.isNaN(n)) return n;
    }
    if (viewSavedDummyMode) {
      const xb = savedViewReconTotals?.xeroBalance;
      if (typeof xb === 'number' && Number.isFinite(xb)) return xb;
      return 0;
    }
    return 10000;
  }, [salesTaxRaw, viewSavedDummyMode, savedViewReconTotals?.xeroBalance]);

  const overpaidUnderpaid = totalA - balanceB;

  const displayGtBalance = savedViewReconTotals?.gtBalance ?? totalA;
  const displayProviderBalance = savedViewReconTotals?.xeroBalance ?? balanceB;
  const displayDifference =
    savedViewReconTotals?.difference ?? overpaidUnderpaid;
  const displayIsReconciled = Math.abs(displayDifference) < 0.005;

  const accountCode = getReconValue(reconSource as any, 'accountCode');
  const displayAccountCode = React.useMemo(() => {
    if (
      viewSavedDummyMode &&
      savedViewReconTotals?.account != null &&
      String(savedViewReconTotals.account).trim() !== ''
    ) {
      return String(savedViewReconTotals.account).trim();
    }
    return accountCode;
  }, [viewSavedDummyMode, savedViewReconTotals?.account, accountCode]);
  const lastLineLabel = React.useMemo(
    () =>
      displayDifference < -0.005
        ? t('organisation.reconciliation-underpaid-tax')
        : displayDifference > 0.005
        ? t('organisation.reconciliation-overpaid-tax')
        : t('organisation.reconciliation-reconciled-balance'),
    [displayDifference, t]
  );

  React.useEffect(() => {
    if (viewSavedDummyMode || !onReconTotalSnapshot) return;
    const savedStatus: DataLogStatus | null =
      openSubmission?.status && String(openSubmission.status).trim().length > 0
        ? (openSubmission.status as DataLogStatus)
        : null;
    onReconTotalSnapshot({
      openDataLogId: dataLogId ?? '',
      openTransactionsCount: Number(a2TransactionCount ?? 0),
      openExceptionsCount: Number(a2ExceptionCount ?? 0),
      totalTaxOpenTransactions: Number(a2TotalTrans ?? 0),
      totalTaxOpenExceptions: Number(a2TotalExc ?? 0),
      gtBalance: totalA,
      xeroBalance: balanceB,
      difference: overpaidUnderpaid,
      totalTaxBalance: balanceBfUsed,
      account: accountCode ?? '',
      savedStatus,
    });
  }, [
    viewSavedDummyMode,
    onReconTotalSnapshot,
    dataLogId,
    a2TransactionCount,
    a2ExceptionCount,
    a2TotalTrans,
    a2TotalExc,
    totalA,
    balanceB,
    overpaidUnderpaid,
    balanceBfUsed,
    accountCode,
    openSubmission?.status,
  ]);

  const convertFiltersToQueryFilters = (
    filters?: Record<string, any>
  ): QueryFilters | undefined => {
    if (!filters) return undefined;
    const converted: QueryFilters = {};
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (Array.isArray(value?.value) && value.value.length > 0) {
        if (key === 'taxName') {
          const expandedIds = value.value.flatMap((name: string) => {
            const normalized = String(name ?? '')
              .trim()
              .toLowerCase();
            return organisationTaxNameToIds.get(normalized) || [name];
          });
          converted[key] = Array.from(new Set(expandedIds));
        } else {
          converted[key] = value.value;
        }
      }
    });
    return Object.keys(converted).length > 0 ? converted : undefined;
  };

  const currentModalTransactionType =
    transactionsModalViewMode === 'exception'
      ? TransactionTypeFilter.EXCEPTION
      : transactionsModalViewMode === 'other'
      ? TransactionTypeFilter.TRANSACTION
      : TransactionTypeFilter.TRANSACTION;

  const currentModalTotal =
    currentModalTransactionType === TransactionTypeFilter.EXCEPTION
      ? pendingExceptionsCount ?? 0
      : transactionsModalViewMode === 'other'
      ? pendingOthersCount ?? 0
      : pendingTransactionsCount ?? 0;
  const reconciliationJournals =
    currentModalTransactionType === TransactionTypeFilter.EXCEPTION
      ? cachedExceptionJournals || []
      : cachedTransactionJournals || [];

  const modalColumns = React.useMemo(() => {
    const baseColumns = getTransactionColumns(
      providerName,
      () => {},
      undefined,
      false,
      true,
      undefined,
      undefined,
      undefined,
      locale
    );

    // Reconciliation "Exceptions NOT submitted" should follow exceptions table
    // behaviour and not show transaction-only exception adornments/actions.
    if (transactionsModalViewMode !== 'exception') return baseColumns;

    return baseColumns
      .filter(
        (col: any) => col?.key !== 'actions' && col?.key !== 'providerIcon'
      )
      .map((col: any) => {
        if (col?.key === 'selectedValue') {
          return {
            ...col,
            render: (content: string) => content,
          };
        }
        if (col?.key === 'taxAmountConverted') {
          return {
            ...col,
            render: (content: number) => (
              <div
                style={{
                  textAlign: 'end',
                  color: content < 0 ? '#d13131' : '',
                }}
              >
                {formatNumber(content)}
              </div>
            ),
          };
        }
        return col;
      });
  }, [transactionsModalViewMode, providerName, locale, formatNumber]);

  const modalTitle = React.useMemo(() => {
    if (transactionsModalSection === 'incomplete') {
      return transactionsModalViewMode === 'exception'
        ? t('organisation.reconciliation-modal-exceptions-not-submitted')
        : transactionsModalViewMode === 'other'
        ? t('organisation.reconciliation-modal-others-not-submitted')
        : t('organisation.reconciliation-modal-pending-incomplete');
    }

    return transactionsModalViewMode === 'exception'
      ? t('organisation.reconciliation-modal-exceptions-not-submitted')
      : transactionsModalViewMode === 'other'
      ? t('organisation.reconciliation-modal-others-not-submitted')
      : t('organisation.reconciliation-modal-not-submitted');
  }, [transactionsModalSection, transactionsModalViewMode, t]);

  const fetchModalTransactions = React.useCallback(
    (
      page: number,
      pageSize: number,
      filters?: QueryFilters,
      transactionType: TransactionTypeFilter = TransactionTypeFilter.TRANSACTION
    ) => {
      const queryKey = JSON.stringify({
        reportId: effectiveReportId,
        page,
        pageSize,
        transactionType,
        groupingType: 'DETAILED',
        filters: filters ?? {},
      });
      if (lastQueryKeyRef.current === queryKey) return;
      lastQueryKeyRef.current = queryKey;

      if (!effectiveReportId) return;
      dispatch(
        queryReconciliationRequest({
          reportId: effectiveReportId,
          page,
          pageSize,
          transactionType,
          groupingType: 'DETAILED',
          filters,
        })
      );
    },
    [dispatch, effectiveReportId]
  );

  const applyOtherFilter = React.useCallback(
    (filters?: QueryFilters): QueryFilters => {
      if (transactionsModalViewMode === 'other') {
        return { ...(filters || {}), selectedValue: ['Other'] };
      }
      if (transactionsModalViewMode === 'transaction') {
        const codes = reconciliationMappingCodesRaw;
        if (codes?.length) {
          const allowed = codes.filter(
            (label) => !isOtherVatMappingLabel(label)
          );
          if (allowed.length > 0) {
            return { ...(filters || {}), selectedValue: allowed };
          }
        }
      }
      return filters || {};
    },
    [transactionsModalViewMode, reconciliationMappingCodesRaw]
  );

  const handleModalTransactionsPageChange = React.useCallback(
    (page: number, pageSize: number, filters?: object) => {
      const parsedFilters = convertFiltersToQueryFilters(
        filters as Record<string, any>
      );
      const effectiveFilters = applyOtherFilter(parsedFilters);
      setTransactionsModalPage(page);
      setTransactionsModalPageSize(pageSize);
      setTransactionsModalFilters(effectiveFilters);
      if (filters && typeof filters === 'object') {
        setTransactionsModalScopeFilters(filters as Record<string, any>);
      }
      fetchModalTransactions(
        page,
        pageSize,
        effectiveFilters,
        currentModalTransactionType
      );
    },
    [
      applyOtherFilter,
      convertFiltersToQueryFilters,
      currentModalTransactionType,
      fetchModalTransactions,
    ]
  );

  const handleModalTransactionsServerFilter = React.useCallback(
    (filters: Record<string, any>, pageSize?: number) => {
      handleModalTransactionsPageChange(
        1,
        pageSize ?? transactionsModalPageSize,
        filters
      );
    },
    [handleModalTransactionsPageChange, transactionsModalPageSize]
  );

  const openTransactionsModal = (
    section: 'not-submitted' | 'incomplete',
    viewMode: 'transaction' | 'exception' | 'other' = 'transaction'
  ) => {
    const transactionType =
      viewMode === 'exception'
        ? TransactionTypeFilter.EXCEPTION
        : TransactionTypeFilter.TRANSACTION;
    setTransactionsModalSection(section);
    setTransactionsModalViewMode(viewMode);
    setTransactionsModalPage(1);
    const excludeOtherFilter = (): QueryFilters | undefined => {
      const codes = reconciliationMappingCodesRaw;
      if (!codes?.length) return undefined;
      const allowed = codes.filter((label) => !isOtherVatMappingLabel(label));
      return allowed.length > 0
        ? ({ selectedValue: allowed } as QueryFilters)
        : undefined;
    };

    const initialFilters =
      viewMode === 'other'
        ? ({ selectedValue: ['Other'] } as QueryFilters)
        : viewMode === 'transaction'
        ? excludeOtherFilter()
        : undefined;
    setTransactionsModalFilters(initialFilters);
    setTransactionsModalOpen(true);
    lastQueryKeyRef.current = '';
    if (viewSavedDummyMode) {
      if (viewMode === 'other') {
        fetchModalTransactions(
          1,
          transactionsModalPageSize,
          { selectedValue: ['Other'] },
          transactionType
        );
      } else {
        fetchModalTransactions(
          1,
          transactionsModalPageSize,
          viewMode === 'transaction' ? excludeOtherFilter() : undefined,
          transactionType
        );
      }
      return;
    }
    if (organisation?.connectionId && mappingId && needsRemoteFilterData) {
      dispatch(
        fetchReconciliationFiltersRequest(organisation.connectionId, mappingId)
      );
    }
    if (viewMode === 'other') {
      fetchModalTransactions(
        1,
        transactionsModalPageSize,
        { selectedValue: ['Other'] },
        transactionType
      );
      return;
    }
    const cachedForType =
      transactionType === TransactionTypeFilter.EXCEPTION
        ? cachedExceptionJournals
        : cachedTransactionJournals;
    if (cachedForType === undefined) {
      fetchModalTransactions(
        1,
        transactionsModalPageSize,
        viewMode === 'transaction' ? excludeOtherFilter() : undefined,
        transactionType
      );
    }
  };

  const providerName =
    organisation?.providerName ??
    t('organisation.reconciliation-provider-placeholder');

  /** User-facing label; backend `providerName` stays raw for API / icons / column logic. */
  const providerDisplayName =
    String(providerName).trim().toLowerCase() === 'zoho'
      ? t(
          'common.zoho',
          'Zoho Books'
        )
      : providerName;

  const transactionsFilterList =
    providerName?.toLowerCase() !== 'zoho'
      ? [
          'taxName',
          'selectedValue',
          'journalDate',
          'journalCreatedDate',
          'sourceType',
          'accountCode',
          'accountType',
          'accountName',
          'netAmountConverted',
        ]
      : [
          'taxName',
          'selectedValue',
          'journalDate',
          'journalCreatedDate',
          'sourceType',
          'netAmountConverted',
        ];

  const downloadPDF = async () => {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('html2canvas'),
    ]);
    setExportingPdf(true);
    const container = document.getElementById('reconciliation-pdf-content');
    if (!container) {
      setExportingPdf(false);
      return;
    }
    try {
      container.classList.add('generating-pdf');
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      );

      const pdf = new jsPDF('p', 'mm', 'a4', true);
      const margin = 10;
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const maxImgW = pageW - margin * 2;

      // Title on first page
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(
        currency
          ? t('organisation.reconciliation-pdf-main-title-currency', {
              currency,
            })
          : t('organisation.reconciliation-tax-control-title'),
        margin,
        20
      );
      if (returnName) {
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.text(returnName, margin, 26);
      }
      if (reconcileDate) {
        pdf.setFontSize(10);
        pdf.text(
          t('organisation.reconciliation-as-at', {
            date: reconcileDate.format('DD/MM/YYYY'),
          }),
          margin,
          32
        );
      }

      const contentTopY = 42;
      const maxImgH = pageH - contentTopY - margin;

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL('image/png');
      const pxW = canvas.width;
      const pxH = canvas.height;

      // Fit full reconciliation block on one page (scale down if needed)
      let drawW = maxImgW;
      let drawH = (pxH * drawW) / pxW;
      if (drawH > maxImgH) {
        drawH = maxImgH;
        drawW = (pxW * drawH) / pxH;
      }
      const drawX = margin + (maxImgW - drawW) / 2;
      pdf.addImage(imgData, 'PNG', drawX, contentTopY, drawW, drawH);

      const orgName =
        organisation?.friendlyName ||
        organisation?.legalName ||
        t('organisation.reconciliation-form-sidebar-tab');
      const fileName = `${t('organisation.reconciliation-pdf-filename', {
        name: orgName,
      })}${
        currency
          ? t('organisation.reconciliation-pdf-filename-currency-suffix', {
              currency,
            })
          : ''
      }.pdf`;
      pdf.save(fileName);
    } finally {
      container.classList.remove('generating-pdf');
      setExportingPdf(false);
    }
  };

  return (
    <ReconciliationLayout>
      <MainSection>
        <TitleWrapper>
          <div>
            <CardTitleLargeSpan style={{ fontSize: 20, lineHeight: '32px' }}>
              {t('organisation.reconciliation-tax-control-title')}
              {currency ? ` (${currency})` : ''}
            </CardTitleLargeSpan>
            {returnName ? (
              <div style={{ fontSize: 14, color: 'var(--color-text-dark, #042a2a)', marginTop: 4 }}>
                {returnName}
              </div>
            ) : null}
          </div>
          <ExportButton
            onClick={downloadPDF}
            disabled={exportingPdf}
            style={{ marginBottom: 0 }}
          >
            {exportingPdf
              ? t('organisation.reconciliation-generating-pdf')
              : t('organisation.reconciliation-download-pdf')}
          </ExportButton>
        </TitleWrapper>

        <ReconciliationPdfContent id="reconciliation-pdf-content">
          <TopSection>
            {reconcileDate ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: viewSavedDummyMode
                    ? 'flex-start'
                    : 'space-between',
                  gap: 16,
                  width: '100%',
                  padding: '0 0 20px 0',
                  borderBottom: '1px solid #e8f0f0',
                }}
              >
                <WorkingsTitle
                  style={{
                    padding: 0,
                    margin: 0,
                    borderBottom: 'none',
                  }}
                >
                  {t('organisation.reconciliation-as-at', {
                    date: reconcileDate.format('DD/MM/YYYY'),
                  })}
                </WorkingsTitle>
                {!viewSavedDummyMode && (
                  <ScopeButton
                    type="default"
                    bg_color="var(--color-surface-raised, #ffffff)"
                    width="150px"
                    height="35px"
                    style={{
                      fontSize: '13px',
                      padding: '4px 5px',
                      margin: 0,
                    }}
                    onClick={onChangeDate}
                  >
                    {t('organisation.reconciliation-change-as-at-date')}
                  </ScopeButton>
                )}
              </div>
            ) : null}
            <TopSectionStatsRow>
              <StatBoxesContainer>
                <StatBox $haveBorder>
                  <GraySpan>
                    {t('organisation.reconciliation-balance-in-gentax')}
                  </GraySpan>
                  <StatValue $negative={displayGtBalance < 0}>
                    {currency ? `${currency} ` : ''}
                    {formatWithParens(displayGtBalance)}
                  </StatValue>
                </StatBox>
                <StatBox $haveBorder>
                  <GraySpan>
                    {t('organisation.reconciliation-balance-in-provider', {
                      provider: providerDisplayName,
                    })}
                  </GraySpan>
                  <StatValue $negative={displayProviderBalance < 0}>
                    {currency ? `${currency} ` : ''}
                    {formatWithParens(displayProviderBalance)}
                  </StatValue>
                </StatBox>
                <StatBox $haveBorder>
                  <GraySpan>
                    {t('organisation.reconciliation-difference')}
                  </GraySpan>
                  {displayIsReconciled ? (
                    <ReconciledLabel>
                      <CheckCircleOutlined style={{ fontSize: 16 }} />
                      {t('organisation.reconciliation-reconciled')}
                    </ReconciledLabel>
                  ) : (
                    <StatValue $negative={displayDifference < 0}>
                      {currency ? `${currency} ` : ''}
                      {formatWithParens(displayDifference)}
                    </StatValue>
                  )}
                </StatBox>
              </StatBoxesContainer>
              {!reconcileDate && !viewSavedDummyMode ? (
                <ScopeButton
                  type="default"
                  bg_color="var(--color-surface-raised, #ffffff)"
                  width="150px"
                  height="35px"
                  style={{
                    fontSize: '13px',
                    padding: '4px 5px',
                    margin: '7px 0 7px 13px',
                  }}
                  onClick={onChangeDate}
                >
                  {t('organisation.reconciliation-change-as-at-date')}
                </ScopeButton>
              ) : null}
            </TopSectionStatsRow>
          </TopSection>

          <WorkingsSection>
            <WorkingsTitle>
              {t('organisation.reconciliation-balance-in-gentax')}
            </WorkingsTitle>
            <ReconRow $taller>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <ReconLabel>
                  {t('organisation.reconciliation-tax-balance-bf-question')}
                </ReconLabel>
                <QuestionMarkTooltip
                  title={t('organisation.reconciliation-balance-bf-tooltip')}
                  iconSize={14}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {currency ? (
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'var(--color-text-dark, #042a2a)',
                      }}
                    >
                      {currency}
                    </span>
                  ) : null}
                  <BalanceBfInputWrapper $viewReadOnly={viewSavedDummyMode}>
                    <Form.Item
                      style={{ marginBottom: 0 }}
                      validateStatus={isBalanceBfTooLarge ? 'error' : ''}
                      help={isBalanceBfTooLarge ? 'Amount too large' : null}
                    >
                      <Input
                        value={balanceBfInputValue}
                        inputMode="decimal"
                        pattern="-?[0-9]*[.]?[0-9]{0,2}"
                        disabled={viewSavedDummyMode}
                        onFocus={() => {
                          if (viewSavedDummyMode) return;
                          const current =
                            balanceBfDisplay ??
                            formatBalanceBf(balanceBf).replace(/,/g, '');
                          setBalanceBfDisplay(balanceBf === 0 ? '' : current);
                        }}
                        onChange={(e) => {
                          if (viewSavedDummyMode) return;
                          const raw = e.target.value.replace(/,/g, '');
                          if (raw === '' || /^-?\d*\.?\d{0,2}$/.test(raw)) {
                            setBalanceBfDisplay(raw);
                            const n = parseFloat(raw);
                            setBalanceBf(Number.isNaN(n) ? 0 : n);
                          }
                        }}
                        onBlur={commitBalanceBfDisplay}
                        style={{
                          width: 136,
                          textAlign: 'right',
                          background: 'var(--color-surface, #f3f7f8)',
                        }}
                        placeholder="0.00"
                      />
                    </Form.Item>
                  </BalanceBfInputWrapper>
                </div>
                {!viewSavedDummyMode ? (
                  <span style={{ alignSelf: 'flex-end' }} data-pdf-hide>
                    <StyledTooltip
                      title={
                        isBalanceBfTooLarge
                          ? 'Amount too large'
                          : canApplyBalanceBf
                          ? t(
                              'organisation.reconciliation-apply-tooltip-active'
                            )
                          : t(
                              'organisation.reconciliation-apply-tooltip-disabled'
                            )
                      }
                    >
                      {canApplyBalanceBf ? (
                        <StyledPopconfirm
                          title={t(
                            'organisation.reconciliation-popconfirm-recalculate-title'
                          )}
                          description={t(
                            'organisation.reconciliation-popconfirm-recalculate-description',
                            { provider: providerDisplayName }
                          )}
                          okText={t('common.apply')}
                          cancelText={t('common.cancel')}
                          overlayStyle={{ maxWidth: 320 }}
                          onConfirm={() => {
                            setBalanceBfUsed(balanceBf);
                            onRecalculate?.(balanceBf);
                          }}
                        >
                          <SaveButtonStyled
                            icon={<SaveOutlined />}
                            aria-label={t(
                              'organisation.reconciliation-apply-tooltip-active'
                            )}
                          >
                            {t('common.apply')}
                          </SaveButtonStyled>
                        </StyledPopconfirm>
                      ) : (
                        <DisabledSaveButton
                          role="img"
                          aria-label={t(
                            'organisation.reconciliation-apply-aria-disabled'
                          )}
                        >
                          <SaveOutlined style={{ fontSize: 12 }} />
                          {t('common.apply')}
                        </DisabledSaveButton>
                      )}
                    </StyledTooltip>
                  </span>
                ) : null}
              </div>
            </ReconRow>

            <Spacer $height={20} />

            {showOpenPeriodSection && (
              <>
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
                >
                  <SectionHeaderRow
                    style={{
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      paddingBottom: 4,
                      paddingTop: 15,
                    }}
                  >
                    <span>
                      {t('organisation.reconciliation-open-periods-header')}
                    </span>
                  </SectionHeaderRow>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flexWrap: 'wrap',
                      paddingLeft: 16,
                      paddingRight: 16,
                      paddingBottom: 12,
                    }}
                  >
                    <StyledTooltip
                      title={t(
                        'organisation.reconciliation-open-submission-tooltip'
                      )}
                    >
                      {vatReportOpenHref ? (
                        <a
                          href={vatReportOpenHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 12,
                            color: '#6b7280',
                            lineHeight: 1.4,
                            textDecoration: 'none',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = 'var(--color-primary, #41aaaf)';
                            e.currentTarget.style.textDecoration = 'underline';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#6b7280';
                            e.currentTarget.style.textDecoration = 'none';
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 8,
                              flexWrap: 'wrap',
                            }}
                          >
                            <span>
                              {openPeriodSummary.periodStr}
                              {openPeriodSummary.dueStr}
                            </span>
                            {openPeriodSummary.status ? (
                              <StatusGetter
                                status={openPeriodSummary.status}
                                smaller
                              />
                            ) : null}
                          </span>
                        </a>
                      ) : (
                        <span
                          style={{
                            fontSize: 12,
                            color: '#6b7280',
                            lineHeight: 1.4,
                            cursor: 'default',
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 8,
                              flexWrap: 'wrap',
                            }}
                          >
                            <span>
                              {openPeriodSummary.periodStr}
                              {openPeriodSummary.dueStr}
                            </span>
                            {openPeriodSummary.status ? (
                              <StatusGetter
                                status={openPeriodSummary.status}
                                smaller
                              />
                            ) : null}
                          </span>
                        </span>
                      )}
                    </StyledTooltip>
                    {vatReportOpenHref ? (
                      <a
                        data-pdf-hide
                        href={vatReportOpenHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        <ScopeButton
                          type="default"
                          width="80px"
                          height="28px"
                          style={{ fontSize: 12 }}
                        >
                          {t('main.open')}
                        </ScopeButton>
                      </a>
                    ) : (
                      <span data-pdf-hide>
                        <ScopeButton
                          type="default"
                          width="80px"
                          height="28px"
                          style={{ fontSize: 12 }}
                          disabled
                        >
                          {t('main.open')}
                        </ScopeButton>
                      </span>
                    )}
                  </div>
                </div>
                {showA2TransactionsRow && (
                  <ReconRow $indent $noBorder>
                    <ReconLabel>
                      {t(
                        'organisation.reconciliation-total-transactions-in-return',
                        {
                          count:
                            a2TransactionCount != null
                              ? formatNumber(a2TransactionCount, {
                                  maximumFractionDigits: 0,
                                  minimumFractionDigits: 0,
                                })
                              : '-',
                        } as any
                      )}
                    </ReconLabel>
                    <ReconValue>{formatWithParens(a2TotalTrans)}</ReconValue>
                  </ReconRow>
                )}
                {showA2ExceptionsRow && (
                  <ReconRow $indent $noBorder>
                    <ReconLabel>
                      {t(
                        'organisation.reconciliation-total-exceptions-in-return',
                        {
                          count:
                            a2ExceptionCount != null
                              ? formatNumber(a2ExceptionCount, {
                                  maximumFractionDigits: 0,
                                  minimumFractionDigits: 0,
                                })
                              : '-',
                        } as any
                      )}
                    </ReconLabel>
                    <ReconValue>{formatWithParens(a2TotalExc)}</ReconValue>
                  </ReconRow>
                )}
                {/* Sub-total always shown for open period when header/period is visible; detail rows only if counts > 0 */}
                <ReconSubTotal $paddingBottom={20}>
                  <SubTotalLabel>
                    {t('organisation.reconciliation-sub-total')}
                  </SubTotalLabel>
                  <SubTotalValue>{formatWithParens(a2SubTotal)}</SubTotalValue>
                </ReconSubTotal>

                <Spacer $height={8} />
              </>
            )}

            <SectionHeaderRow>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {t('organisation.reconciliation-unsubmitted-section')}
                <StyledTooltip
                  title={t('organisation.reconciliation-unsubmitted-tooltip', {
                    date: reconcileDate
                      ? reconcileDate.format('DD/MM/YYYY')
                      : t('organisation.reconciliation-undated-placeholder'),
                  })}
                  placement="top"
                >
                  <span style={{ display: 'inline-flex', cursor: 'help' }}>
                    <QuestionCircleOutlined
                      style={{ color: 'var(--color-text-muted, #5a6a6a)', fontSize: 14 }}
                    />
                  </span>
                </StyledTooltip>
              </span>
            </SectionHeaderRow>
            {showA1TransactionsRow && (
              <ReconRow $indent $noBorder>
                <Space size={8} align="center">
                  <ReconLabel>
                    {t('organisation.reconciliation-pending-transactions', {
                      count:
                        pendingTransactionsCount != null
                          ? formatNumber(pendingTransactionsCount, {
                              maximumFractionDigits: 0,
                              minimumFractionDigits: 0,
                            })
                          : '-',
                    } as any)}
                  </ReconLabel>
                  <span data-pdf-hide>
                    <ScopeButton
                      type="default"
                      width="80px"
                      height="28px"
                      style={{ fontSize: 12 }}
                      disabled={!effectiveReportId}
                      onClick={() =>
                        openTransactionsModal('not-submitted', 'transaction')
                      }
                    >
                      {t('organisation.reconciliation-view')}
                    </ScopeButton>
                  </span>
                </Space>
                <ReconValue>{formatWithParens(a1TotalTrans)}</ReconValue>
              </ReconRow>
            )}
            {showA1ExceptionsRow && (
              <ReconRow $indent $noBorder>
                <Space size={8} align="center">
                  <ReconLabel>
                    {t('organisation.reconciliation-pending-exceptions', {
                      count:
                        pendingExceptionsCount != null
                          ? formatNumber(pendingExceptionsCount, {
                              maximumFractionDigits: 0,
                              minimumFractionDigits: 0,
                            })
                          : '-',
                    } as any)}
                  </ReconLabel>
                  <span data-pdf-hide>
                    <ScopeButton
                      type="default"
                      width="80px"
                      height="28px"
                      style={{ fontSize: 12 }}
                      disabled={!effectiveReportId}
                      onClick={() =>
                        openTransactionsModal('incomplete', 'exception')
                      }
                    >
                      {t('organisation.reconciliation-view')}
                    </ScopeButton>
                  </span>
                </Space>
                <ReconValue>{formatWithParens(a1TotalExc)}</ReconValue>
              </ReconRow>
            )}
            {showA1OthersRow && (
              <ReconRow $indent $noBorder>
                <Space size={8} align="center">
                  <ReconLabel>
                    {`Total others (${
                      pendingOthersCount != null
                        ? formatNumber(pendingOthersCount, {
                            maximumFractionDigits: 0,
                            minimumFractionDigits: 0,
                          })
                        : '-'
                    })`}
                  </ReconLabel>
                  <span data-pdf-hide>
                    <ScopeButton
                      type="default"
                      width="80px"
                      height="28px"
                      style={{ fontSize: 12 }}
                      disabled={!effectiveReportId}
                      onClick={() =>
                        openTransactionsModal('incomplete', 'other')
                      }
                    >
                      {t('organisation.reconciliation-view')}
                    </ScopeButton>
                  </span>
                </Space>
                <ReconValue>{formatWithParens(a1TotalOther)}</ReconValue>
              </ReconRow>
            )}
            {/* Sub-total always shown for unsubmitted section; detail rows only when count > 0 */}
            <ReconSubTotal $noBorder>
              <SubTotalLabel>
                {t('organisation.reconciliation-sub-total')}
              </SubTotalLabel>
              <SubTotalValue>{formatWithParens(a1SubTotal)}</SubTotalValue>
            </ReconSubTotal>

            <Spacer $height={24} />

            <TotalUnsubmittedRow $paddingBottom={20}>
              <SubTotalRowLabel $bold>
                {t('organisation.reconciliation-balance-in-gentax')}
                <span
                  style={{
                    color: 'var(--color-text-muted, #5a6a6a)',
                    fontSize: 12,
                    fontWeight: 400,
                    marginLeft: 4,
                  }}
                >
                  {t('organisation.reconciliation-total-unsubmitted-subtext')}
                </span>
              </SubTotalRowLabel>
              <TotalUnsubmittedValue>
                {formatWithParens(totalA)}
              </TotalUnsubmittedValue>
            </TotalUnsubmittedRow>

            <Spacer $height={24} />

            <WorkingsTitle>
              {t('organisation.reconciliation-balance-in-provider', {
                provider: providerDisplayName,
              })}
            </WorkingsTitle>

            <ReconRow>
              <div
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}
              >
                {organisation?.providerName &&
                  providerIcons[organisation.providerName.toLowerCase()] && (
                    <div
                      style={{
                        width: 32,
                        minWidth: 32,
                        height: 32,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={
                          providerIcons[organisation.providerName.toLowerCase()]
                        }
                        alt={providerDisplayName}
                        style={{
                          maxWidth: 28,
                          maxHeight: 28,
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          borderRadius: '50%',
                        }}
                      />
                    </div>
                  )}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    paddingBottom: 20,
                  }}
                >
                  <ReconLabel $bold style={{ fontSize: 14 }}>
                    {t(
                      'organisation.reconciliation-provider-tax-balance-label',
                      { provider: providerDisplayName }
                    )}
                  </ReconLabel>
                  {displayAccountCode != null && displayAccountCode !== '' && (
                    <ReconLabel style={{ fontWeight: 400 }}>
                      {t('organisation.reconciliation-account-code', {
                        code: displayAccountCode,
                      })}
                    </ReconLabel>
                  )}
                </div>
              </div>
              <ReconValue $bold style={{ fontSize: 14 }}>
                {formatWithParens(displayProviderBalance)}
              </ReconValue>
            </ReconRow>

            <OverpaidUnderpaidRow>
              <ReconLabel $bold>{lastLineLabel}</ReconLabel>
              <ReconValue $bold $red={displayDifference < 0}>
                {formatWithParens(displayDifference)}
              </ReconValue>
            </OverpaidUnderpaidRow>
          </WorkingsSection>
        </ReconciliationPdfContent>

        <Modal
          title={modalTitle}
          open={transactionsModalOpen}
          onCancel={() => {
            setTransactionsModalOpen(false);
            setTransactionsModalSection(null);
            setTransactionsModalViewMode('transaction');
            setTransactionsModalPage(1);
            setTransactionsModalFilters(undefined);
            lastQueryKeyRef.current = '';
          }}
          footer={null}
          width="90vw"
          style={{ maxWidth: 1280 }}
          styles={{ body: { maxHeight: '80vh', overflow: 'auto' } }}
        >
          <Spin spinning={reconciliationQueryLoading}>
            <div style={{ padding: '0 0 16px' }}>
              <TransactionTableHeader
                journalType="DETAILED"
                noSumData={true}
                onSearch={() => {}}
                onJournalTypeChange={undefined}
                t={t}
                exportToCsv={() => {}}
                translationNamespace={
                  transactionsModalViewMode === 'exception'
                    ? 'exceptions'
                    : transactionsModalViewMode === 'other'
                    ? 'transactions'
                    : 'transactions'
                }
                itemLabel={
                  transactionsModalViewMode === 'exception'
                    ? t('vat-report.exceptions')
                    : t('organisation.transactions')
                }
              />
              <Divider />
              <TransactionTableWrapper>
                <ScopeTableFilters
                  filters={transactionsFilterList}
                  mainLocaleKey={
                    transactionsModalViewMode === 'exception'
                      ? 'exceptions'
                      : 'transactions'
                  }
                  value={transactionsModalScopeFilters}
                  onChange={setTransactionsModalScopeFilters}
                  originalDataSource={reconciliationJournals || []}
                  tableDataSource={reconciliationJournals || []}
                  customFilterOptions={reconciliationCustomFilterOptions}
                  customPagination={true}
                  onServerFilter={handleModalTransactionsServerFilter}
                  embedded={false}
                />
                <ScopeTable
                  className="transaction-table"
                  externalFiltersState={transactionsModalScopeFilters}
                  tableDataSource={reconciliationJournals || []}
                  originalDataSource={reconciliationJournals || []}
                  columns={modalColumns}
                  pagination={true}
                  rowsPerPage={20}
                  enableColumnsCustomization={true}
                  visibleColumns={transactionsColumnConfig.visibleColumns}
                  lockedColumns={transactionsColumnConfig.lockedColumns}
                  columnOrder={transactionsColumnConfig.columnOrder}
                  onColumnsChange={handleTransactionsColumnsChange}
                  alwaysFixedColumns={[
                    'netAmountConverted',
                    'taxAmountConverted',
                  ]}
                  rowHeight={40}
                  showSizeChanger
                  getNewPageSize={(newPageSize: number) => {
                    handleModalTransactionsPageChange(
                      1,
                      newPageSize,
                      transactionsModalScopeFilters
                    );
                  }}
                  scroll={{ x: 'max-content' }}
                  fontSize={12}
                  reportId={effectiveReportId || ''}
                  transactionType={currentModalTransactionType}
                  customPagination={true}
                  total={currentModalTotal}
                  onPageChange={handleModalTransactionsPageChange}
                />
              </TransactionTableWrapper>
            </div>
          </Spin>
        </Modal>
      </MainSection>
    </ReconciliationLayout>
  );
};
